import {
    CalendarDate,
    CommunityId,
    DefaultReferences,
    EMPTY_REFERRAL_SENTINEL,
    PromptId,
    UserId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { DateRangeControl, DateRangeValue } from "components/reports/date-presets";
import { defaultCardStyle } from "components/ui/card";
import { SpMultiSelect } from "components/ui/sp-multi-select";
import { Spinner } from "components/ui/spinner";
import { useCommunityNamesQuery, useGetUsersForRegionQuery } from "generated/graphql";
import React from "react";
import { useCommunityTypeQuestion, useRegionId, useRegions } from "store/selectors/hooks";
import { stableQueryOptions } from "util/requests";
import { ContentContainer, ContentFull, PageHeader, Stage } from "../../layout";
import { trpc } from "../../rpc/trpc";

type RouterOutput = inferRouterOutputs<AppRouter>;
type StageConversionData = RouterOutput["stageConversion"]["stageConversion"];
type StageRow = StageConversionData["stages"][number];
type AllUsersData = RouterOutput["stageConversion"]["stageConversionAllUsers"];
type UserBreakdownRow = AllUsersData["userBreakdowns"][number];

const cancelWhenSuperseded = { trpc: { abortOnUnmount: true } };

function escapeCsvValue(value: string | number): string {
    const str = String(value);
    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

function triggerCsvDownload(rows: (string | number)[][], filename: string): void {
    const csvContent = rows.map(row => row.map(escapeCsvValue).join(",")).join("\n");
    const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
}

function buildCsvFilename(startDate: string, endDate: string): string {
    return `stage-conversion-${startDate}-to-${endDate}.csv`;
}

function generateSingleUserCsv(
    stages: StageRow[],
    teamStages: StageRow[] | undefined,
    advisorName: string | undefined,
): (string | number)[][] {
    if (teamStages && advisorName) {
        const teamMap = new Map<string, StageRow>();
        for (const s of teamStages) {
            teamMap.set(s.stageId, s);
        }
        const header = [
            "Stage",
            `Reach Rate (${advisorName})`,
            `Move Rate (${advisorName})`,
            "Team Reach Rate",
            "Team Move Rate",
            "Reach Rate Variance (pp)",
            "Move Rate Variance (pp)",
        ];
        const dataRows: (string | number)[][] = [header];
        for (const stage of stages) {
            const teamRow = teamMap.get(stage.stageId);
            dataRows.push([
                stage.stageName,
                stage.reachRate,
                stage.moveRate,
                teamRow?.reachRate ?? "",
                teamRow?.moveRate ?? "",
                teamRow ? stage.reachRate - teamRow.reachRate : "",
                teamRow ? stage.moveRate - teamRow.moveRate : "",
            ]);
        }
        return dataRows;
    }
    const header = ["Stage", "Reach Rate", "Move Rate", "Clients"];
    const dataRows: (string | number)[][] = [header];
    for (const stage of stages) {
        dataRows.push([
            stage.stageName,
            stage.reachRate,
            stage.moveRate,
            stage.clientCount,
        ]);
    }
    return dataRows;
}

function generateMatrixCsv(
    teamStages: StageRow[],
    advisorBreakdowns: UserBreakdownRow[],
    userNameMap: Map<string, string>,
): (string | number)[][] {
    const header: (string | number)[] = [
        "Stage",
        "Team Reach Rate",
        "Team Move Rate",
        "Team Clients",
    ];
    for (const ub of advisorBreakdowns) {
        const name = userNameMap.get(ub.userId) ?? "Unknown";
        header.push(
            `${name} Reach Rate`,
            `${name} Move Rate`,
            `${name} Clients`,
            `${name} Reach Variance (pp)`,
            `${name} Move Variance (pp)`,
        );
    }

    const userStageMaps = new Map<string, Map<string, StageRow>>();
    for (const ub of advisorBreakdowns) {
        const stageMap = new Map<string, StageRow>();
        for (const s of ub.stages) {
            stageMap.set(s.stageId, s);
        }
        userStageMaps.set(ub.userId, stageMap);
    }

    const dataRows: (string | number)[][] = [header];
    for (const stage of teamStages) {
        const row: (string | number)[] = [
            stage.stageName,
            stage.reachRate,
            stage.moveRate,
            stage.clientCount,
        ];
        for (const ub of advisorBreakdowns) {
            const userStage = userStageMaps.get(ub.userId)?.get(stage.stageId);
            row.push(
                userStage?.reachRate ?? 0,
                userStage?.moveRate ?? 0,
                userStage?.clientCount ?? 0,
                userStage ? userStage.reachRate - stage.reachRate : 0,
                userStage ? userStage.moveRate - stage.moveRate : 0,
            );
        }
        dataRows.push(row);
    }
    return dataRows;
}

const DownloadCsvButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    return (
        <button
            onClick={onClick}
            className="px-3 py-1.5 text-sm font-medium rounded bg-gray-100 text-gray-700 hover:bg-gray-200">
            Download CSV
        </button>
    );
};

export const StageConversion: React.FC = () => {
    const [dateRange, setDateRange] = React.useState<DateRangeValue>(() => {
        const today = CalendarDate.today;
        const startDate =
            today.month === 1
                ? new CalendarDate(today.year - 1, 1, today.day)
                : new CalendarDate(today.year - 1, today.month, today.day);
        return { startDate, endDate: today, activePreset: null };
    });
    const [selectedUserIds, setSelectedUserIds] = React.useState<UserId[]>([]);
    const [filtersExpanded, setFiltersExpanded] = React.useState(false);
    const [selectedReferralSources, setSelectedReferralSources] = React.useState<
        string[]
    >([]);
    const [selectedCareTypes, setSelectedCareTypes] = React.useState<PromptId[]>([]);
    const regionId = useRegionId();
    const { startDate, endDate } = dateRange;
    const validQuery = regionId && startDate && endDate;

    const users = useGetUsersForRegionQuery(
        { regionId },
        { ...stableQueryOptions(), enabled: !!regionId },
    );

    const activeUsers = React.useMemo(() => {
        if (!users.data) return [];
        return users.data.getUsersForRegion
            .filter(u => !u.hidden && !u.disabled)
            .sort((a, b) =>
                a.preferredName
                    .toLocaleLowerCase()
                    .localeCompare(b.preferredName.toLocaleLowerCase()),
            );
    }, [users.data]);

    const advisorOptions = React.useMemo(
        () => activeUsers.map(u => ({ id: u.id, label: u.preferredName })),
        [activeUsers],
    );

    const referralSourceOptions = React.useMemo(
        () =>
            DefaultReferences.filter(r => r.key !== EMPTY_REFERRAL_SENTINEL).map(r => ({
                id: r.key,
                label: r.text,
            })),
        [],
    );

    const regions = useRegions();
    const region = regions.find(r => r.id === regionId);
    const communityTypeQuestion = useCommunityTypeQuestion(region?.key, regionId);

    const careTypeOptions = React.useMemo(() => {
        if (!communityTypeQuestion?.prompts) return [];
        return communityTypeQuestion.prompts
            .filter(p => !p.deleted)
            .map(p => ({ id: p.id as PromptId, label: p.title || "" }));
    }, [communityTypeQuestion]);

    const [selectedCommunities, setSelectedCommunities] = React.useState<CommunityId[]>(
        [],
    );
    const communityNamesData = useCommunityNamesQuery(
        { regionKey: region?.key ?? "" },
        { ...stableQueryOptions(), enabled: !!region?.key },
    );

    const communityOptions = React.useMemo(() => {
        if (!communityNamesData.data?.communityNames?.communities) return [];
        return communityNamesData.data.communityNames.communities
            .filter(c => c.name)
            .map(c => ({ id: c.id as CommunityId, label: c.name }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [communityNamesData.data]);

    return (
        <Stage>
            <PageHeader title="Stage Conversion" stackBreakpoint="md">
                <DateRangeControl value={dateRange} onChange={setDateRange} />
            </PageHeader>
            <div className="px-4 py-3 border-b border-gray-200">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="w-full sm:w-128">
                        <SpMultiSelect
                            label="Advisor"
                            placeholder="Team Average"
                            options={advisorOptions}
                            value={selectedUserIds}
                            onChange={setSelectedUserIds}
                        />
                    </div>
                    <button
                        onClick={() => setFiltersExpanded(!filtersExpanded)}
                        className="md:hidden px-3 py-1.5 text-sm font-medium rounded bg-gray-100 text-gray-700 hover:bg-gray-200">
                        {filtersExpanded ? "Hide Filters" : "More Filters"}
                    </button>
                </div>
                <div
                    className={`${filtersExpanded ? "" : "hidden"} md:flex md:flex-wrap items-end gap-3 mt-3 space-y-2 md:space-y-0`}>
                    <div>
                        <SpMultiSelect
                            label="Referral Source"
                            options={referralSourceOptions}
                            value={selectedReferralSources}
                            placeholder="All sources"
                            onChange={setSelectedReferralSources}
                        />
                    </div>
                    {careTypeOptions.length > 0 ? (
                        <div>
                            <SpMultiSelect
                                label="Care Type"
                                options={careTypeOptions}
                                value={selectedCareTypes}
                                placeholder="All types"
                                onChange={setSelectedCareTypes}
                            />
                        </div>
                    ) : null}
                    {communityOptions.length > 0 ? (
                        <div>
                            <SpMultiSelect
                                label="Community"
                                options={communityOptions}
                                value={selectedCommunities}
                                placeholder="All communities"
                                onChange={setSelectedCommunities}
                            />
                        </div>
                    ) : null}
                </div>
            </div>
            <ContentContainer>
                {validQuery && selectedUserIds.length >= 1 ? (
                    <AdvisorMatrixContent
                        startDate={startDate.toString()}
                        endDate={endDate.toString()}
                        regionId={regionId}
                        referralSourceIds={
                            selectedReferralSources.length > 0
                                ? selectedReferralSources
                                : undefined
                        }
                        careTypeIds={
                            selectedCareTypes.length > 0 ? selectedCareTypes : undefined
                        }
                        communityIds={
                            selectedCommunities.length > 0
                                ? selectedCommunities
                                : undefined
                        }
                        activeUsers={activeUsers}
                        selectedUserIds={selectedUserIds}
                    />
                ) : validQuery ? (
                    <StageConversionContent
                        startDate={startDate.toString()}
                        endDate={endDate.toString()}
                        regionId={regionId}
                        referralSourceIds={
                            selectedReferralSources.length > 0
                                ? selectedReferralSources
                                : undefined
                        }
                        careTypeIds={
                            selectedCareTypes.length > 0 ? selectedCareTypes : undefined
                        }
                        communityIds={
                            selectedCommunities.length > 0
                                ? selectedCommunities
                                : undefined
                        }
                    />
                ) : null}
            </ContentContainer>
        </Stage>
    );
};

const StageConversionContent: React.FC<{
    startDate: string;
    endDate: string;
    regionId: string;
    userId?: string;
    advisorName?: string;
    referralSourceIds?: string[];
    careTypeIds?: string[];
    communityIds?: string[];
}> = ({
    startDate,
    endDate,
    regionId,
    userId,
    advisorName,
    referralSourceIds,
    careTypeIds,
    communityIds,
}) => {
    const trpcClient = trpc.useTRPC();
    const baseParams = {
        startDate,
        endDate,
        regionId,
        referralSourceIds,
        careTypeIds,
        communityIds,
    };

    const advisorQuery = useQuery({
        ...trpcClient.stageConversion.stageConversion.queryOptions(
            { ...baseParams, userId: userId! },
            cancelWhenSuperseded,
        ),
        enabled: !!userId,
    });

    const teamQuery = useQuery(
        trpcClient.stageConversion.stageConversion.queryOptions(
            baseParams,
            cancelWhenSuperseded,
        ),
    );

    const isLoading = teamQuery.isLoading || (!!userId && advisorQuery.isLoading);
    const error = teamQuery.error || advisorQuery.error;
    const teamData = teamQuery.data;
    const advisorData = userId ? advisorQuery.data : undefined;
    const primaryData = advisorData ?? teamData;

    const advisorLowSample =
        !!advisorData &&
        advisorData.totalActiveClients > 0 &&
        advisorData.totalActiveClients < 10;

    const userInsight = React.useMemo(() => {
        if (!advisorData || !teamData || !advisorName) return undefined;
        if (advisorData.totalActiveClients < 10) return undefined;

        const teamMap = new Map<string, StageRow>();
        for (const s of teamData.stages) {
            teamMap.set(s.stageId, s);
        }

        const diffs: {
            stageName: string;
            advisorRate: number;
            teamRate: number;
            diff: number;
        }[] = [];
        for (const stage of advisorData.stages) {
            const teamStage = teamMap.get(stage.stageId);
            if (!teamStage) continue;
            diffs.push({
                stageName: stage.stageName,
                advisorRate: stage.reachRate,
                teamRate: teamStage.reachRate,
                diff: stage.reachRate - teamStage.reachRate,
            });
        }

        if (diffs.length === 0) return undefined;

        const hasNegativeGap = diffs.some(d => d.diff < 0);

        if (hasNegativeGap) {
            const worst = diffs.reduce((min, d) => (d.diff < min.diff ? d : min));
            const pp = Math.abs(Math.round(worst.diff));
            return `${advisorName}\u2019s biggest gap vs. team average is at ${worst.stageName}: ${worst.advisorRate}% vs. ${worst.teamRate}% (\u2212${pp}pp).`;
        }

        const best = diffs.reduce((max, d) => (d.diff > max.diff ? d : max));
        const pp = Math.round(best.diff);
        return `${advisorName}\u2019s strongest stage vs. team average is ${best.stageName}: ${best.advisorRate}% vs. ${best.teamRate}% (+${pp}pp).`;
    }, [advisorData, teamData, advisorName]);

    const allInsights = React.useMemo(() => {
        const base = primaryData?.insights ?? [];
        if (userInsight) return [...base, userInsight];
        return base;
    }, [primaryData?.insights, userInsight]);

    if (isLoading) {
        return (
            <ContentFull>
                <Spinner />
            </ContentFull>
        );
    }

    if (error) {
        return (
            <ContentFull>
                <p className="text-red-600">
                    {(error as { message?: string })?.message ?? "Failed to load report"}
                </p>
            </ContentFull>
        );
    }

    if (!primaryData || primaryData.totalActiveClients === 0) {
        return (
            <ContentFull>
                <div className="text-center py-12 text-gray-500">
                    <p className="text-lg font-medium">No data available</p>
                    <p className="mt-1 text-sm">
                        No active clients found in the selected date range.
                    </p>
                </div>
            </ContentFull>
        );
    }

    return (
        <div className="space-y-4">
            <SummaryBar totalActiveClients={primaryData.totalActiveClients} />
            <div className="flex justify-end">
                <DownloadCsvButton
                    onClick={() => {
                        triggerCsvDownload(
                            generateSingleUserCsv(
                                primaryData.stages,
                                advisorData ? teamData?.stages : undefined,
                                advisorName,
                            ),
                            buildCsvFilename(startDate, endDate),
                        );
                    }}
                />
            </div>
            {advisorLowSample ? (
                <LowSampleNotice totalClients={advisorData.totalActiveClients} />
            ) : null}
            <ConversionTable
                stages={primaryData.stages}
                teamStages={advisorData ? teamData?.stages : undefined}
                advisorName={advisorName}
                advisorTotalClients={advisorData?.totalActiveClients}
            />
            {allInsights.length > 0 ? <InsightStrip insights={allInsights} /> : null}
        </div>
    );
};

const SummaryBar: React.FC<{ totalActiveClients: number }> = ({ totalActiveClients }) => {
    return (
        <ContentFull>
            <div className="flex items-center space-x-4">
                <div>
                    <span className="text-xs font-medium text-gray-500">
                        Active Clients
                    </span>
                    <span className="ml-2 text-lg font-semibold text-zinc-950">
                        {totalActiveClients}
                    </span>
                </div>
            </div>
        </ContentFull>
    );
};

const LowSampleNotice: React.FC<{ totalClients: number }> = ({ totalClients }) => {
    return (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Low sample size {"\u2014"} results may not be statistically meaningful (n ={" "}
            {totalClients})
        </div>
    );
};

const TAG_STYLES: Record<string, { bg: string; text: string; label: string }> = {
    highDropoff: {
        bg: "bg-amber-100",
        text: "text-amber-800",
        label: "High Dropoff",
    },
    strongMovePredictor: {
        bg: "bg-emerald-100",
        text: "text-emerald-800",
        label: "Strong Move Predictor",
    },
};

const ConversionTable: React.FC<{
    stages: StageRow[];
    teamStages?: StageRow[];
    advisorName?: string;
    advisorTotalClients?: number;
}> = ({ stages, teamStages, advisorName, advisorTotalClients }) => {
    const maxReach = Math.max(...stages.map(s => s.reachRate), 1);
    const teamStageMap = React.useMemo(() => {
        if (!teamStages) return undefined;
        const map = new Map<string, StageRow>();
        for (const s of teamStages) {
            map.set(s.stageId, s);
        }
        return map;
    }, [teamStages]);

    const showVariance =
        !!teamStageMap && advisorTotalClients !== undefined && advisorTotalClients >= 10;

    const reachLabel = advisorName ? `Reach Rate (${advisorName})` : "Reach Rate";
    const moveLabel = advisorName ? `Move Rate (${advisorName})` : "Move Rate";

    return (
        <div className={`overflow-x-auto ${defaultCardStyle}`}>
            <table className="w-full">
                <thead>
                    <tr>
                        <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-xs text-left md:w-48">
                            Stage
                        </th>
                        <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-xs text-left">
                            {reachLabel}
                        </th>
                        <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-xs text-left">
                            {moveLabel}
                        </th>
                        <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-xs text-right w-24">
                            Clients
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {stages.map((stage, i) => {
                        const teamRow = teamStageMap?.get(stage.stageId);
                        return (
                            <tr
                                key={stage.stageId}
                                className={`border-b border-gray-200 hover:bg-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                                <td className="py-2.5 px-3">
                                    <div className="text-sm font-medium text-zinc-950">
                                        {stage.stageName}
                                    </div>
                                    {stage.tags.length > 0 ? (
                                        <div className="flex gap-1 mt-1">
                                            {stage.tags.map(tag => {
                                                const style = TAG_STYLES[tag];
                                                return style ? (
                                                    <span
                                                        key={tag}
                                                        className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${style.bg} ${style.text}`}>
                                                        {style.label}
                                                    </span>
                                                ) : null;
                                            })}
                                        </div>
                                    ) : null}
                                </td>
                                <td className="py-2.5 px-3">
                                    <DualRateBar
                                        rate={stage.reachRate}
                                        teamRate={teamRow?.reachRate}
                                        maxRate={maxReach}
                                        color="#2A6496"
                                    />
                                    {showVariance && teamRow ? (
                                        <VarianceBadge
                                            advisorRate={stage.reachRate}
                                            teamRate={teamRow.reachRate}
                                        />
                                    ) : null}
                                </td>
                                <td className="py-2.5 px-3">
                                    <DualRateBar
                                        rate={stage.moveRate}
                                        teamRate={teamRow?.moveRate}
                                        maxRate={100}
                                        color="#2D7A4F"
                                    />
                                    {showVariance && teamRow ? (
                                        <VarianceBadge
                                            advisorRate={stage.moveRate}
                                            teamRate={teamRow.moveRate}
                                        />
                                    ) : null}
                                </td>
                                <td className="py-2.5 px-3 text-sm text-right tabular-nums text-zinc-950">
                                    {stage.clientCount}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const VarianceBadge: React.FC<{
    advisorRate: number;
    teamRate: number;
}> = ({ advisorRate, teamRate }) => {
    const diff = Math.round(advisorRate - teamRate);
    if (Math.abs(diff) < 10) return null;
    const isPositive = diff > 0;
    const bgColor = isPositive ? "bg-emerald-100" : "bg-orange-100";
    const textColor = isPositive ? "text-emerald-800" : "text-orange-800";
    const sign = isPositive ? "+" : "\u2212";

    return (
        <span
            className={`block mt-1 mx-auto w-fit px-1.5 py-0.5 text-[10px] font-medium rounded ${bgColor} ${textColor}`}>
            {sign}
            {Math.abs(diff)}pp vs. team
        </span>
    );
};

const DualRateBar: React.FC<{
    rate: number;
    teamRate?: number;
    maxRate: number;
    color: string;
}> = ({ rate, teamRate, maxRate, color }) => {
    const width = maxRate > 0 ? (rate / maxRate) * 100 : 0;
    const teamWidth =
        teamRate !== undefined && maxRate > 0 ? (teamRate / maxRate) * 100 : undefined;

    return (
        <div className="flex items-center md:space-x-2">
            <div className="relative flex-1 h-4 bg-gray-100 rounded overflow-hidden hidden md:block">
                {teamWidth !== undefined ? (
                    <div
                        className="absolute inset-y-0 left-0 rounded"
                        style={{
                            width: `${teamWidth}%`,
                            border: `1.5px dashed ${color}`,
                            opacity: 0.4,
                            minWidth: teamRate && teamRate > 0 ? 4 : 0,
                        }}
                    />
                ) : null}
                <div
                    className="h-full rounded relative"
                    style={{
                        width: `${width}%`,
                        backgroundColor: color,
                        minWidth: rate > 0 ? 4 : 0,
                    }}
                />
            </div>
            <span className="text-xs font-medium tabular-nums text-zinc-700 w-12 text-right">
                {rate}%
            </span>
        </div>
    );
};

type ActiveUser = { id: string; preferredName: string };

const AdvisorMatrixContent: React.FC<{
    startDate: string;
    endDate: string;
    regionId: string;
    referralSourceIds?: string[];
    careTypeIds?: string[];
    communityIds?: string[];
    activeUsers: ActiveUser[];
    selectedUserIds: UserId[];
}> = ({
    startDate,
    endDate,
    regionId,
    referralSourceIds,
    careTypeIds,
    communityIds,
    activeUsers,
    selectedUserIds,
}) => {
    const trpcClient = trpc.useTRPC();
    const query = useQuery(
        trpcClient.stageConversion.stageConversionAllUsers.queryOptions(
            {
                startDate,
                endDate,
                regionId,
                referralSourceIds,
                careTypeIds,
                communityIds,
            },
            cancelWhenSuperseded,
        ),
    );

    const userNameMap = React.useMemo(() => {
        const map = new Map<string, string>();
        for (const u of activeUsers) {
            map.set(u.id, u.preferredName);
        }
        return map;
    }, [activeUsers]);

    const filteredBreakdowns = React.useMemo(() => {
        if (!query.data) return [];
        const breakdownMap = new Map<string, UserBreakdownRow>();
        for (const ub of query.data.userBreakdowns) {
            breakdownMap.set(ub.userId, ub);
        }
        return selectedUserIds
            .map(
                userId =>
                    breakdownMap.get(userId) ?? {
                        userId,
                        totalClients: 0,
                        stages: [],
                    },
            )
            .sort((a, b) => {
                const nameA = userNameMap.get(a.userId) ?? "";
                const nameB = userNameMap.get(b.userId) ?? "";
                return nameA.toLocaleLowerCase().localeCompare(nameB.toLocaleLowerCase());
            });
    }, [query.data, selectedUserIds, userNameMap]);

    if (query.isLoading) {
        return (
            <ContentFull>
                <Spinner />
            </ContentFull>
        );
    }

    if (query.error) {
        return (
            <ContentFull>
                <p className="text-red-600">
                    {(query.error as { message?: string })?.message ??
                        "Failed to load report"}
                </p>
            </ContentFull>
        );
    }

    if (!query.data || query.data.teamResult.totalActiveClients === 0) {
        return (
            <ContentFull>
                <div className="text-center py-12 text-gray-500">
                    <p className="text-lg font-medium">No data available</p>
                    <p className="mt-1 text-sm">
                        No active clients found in the selected date range.
                    </p>
                </div>
            </ContentFull>
        );
    }

    return (
        <div className="space-y-4">
            <SummaryBar totalActiveClients={query.data.teamResult.totalActiveClients} />
            <div className="flex justify-end">
                <DownloadCsvButton
                    onClick={() => {
                        triggerCsvDownload(
                            generateMatrixCsv(
                                query.data!.teamResult.stages,
                                filteredBreakdowns,
                                userNameMap,
                            ),
                            buildCsvFilename(startDate, endDate),
                        );
                    }}
                />
            </div>
            <AdvisorMatrix
                teamStages={query.data.teamResult.stages}
                advisorBreakdowns={filteredBreakdowns}
                userNameMap={userNameMap}
            />
            {query.data.teamResult.insights.length > 0 ? (
                <InsightStrip insights={query.data.teamResult.insights} />
            ) : null}
        </div>
    );
};

const AdvisorMatrix: React.FC<{
    teamStages: StageRow[];
    advisorBreakdowns: UserBreakdownRow[];
    userNameMap: Map<string, string>;
}> = ({ teamStages, advisorBreakdowns, userNameMap }) => {
    const userStageMaps = React.useMemo(() => {
        const maps = new Map<string, Map<string, StageRow>>();
        for (const ub of advisorBreakdowns) {
            const stageMap = new Map<string, StageRow>();
            for (const s of ub.stages) {
                stageMap.set(s.stageId, s);
            }
            maps.set(ub.userId, stageMap);
        }
        return maps;
    }, [advisorBreakdowns]);

    return (
        <div className={`overflow-x-auto ${defaultCardStyle}`}>
            <table className="w-full">
                <thead>
                    <tr>
                        <th
                            rowSpan={2}
                            className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-xs text-left align-bottom md:w-48">
                            Stage
                        </th>
                        <th
                            colSpan={3}
                            className="py-1 px-3 bg-gray-200 text-gray-600 uppercase text-xs text-center border-l border-gray-300">
                            Team Average
                        </th>
                        {advisorBreakdowns.map(ub => (
                            <th
                                key={ub.userId}
                                colSpan={3}
                                className="py-1 px-3 bg-gray-200 text-zinc-950 text-xs font-semibold text-center border-l border-gray-300">
                                {userNameMap.get(ub.userId) ?? "Unknown"}
                            </th>
                        ))}
                    </tr>
                    <tr>
                        <th className="py-1 px-3 bg-gray-200 text-gray-600 uppercase text-[10px] text-center border-l border-gray-300">
                            Reach
                        </th>
                        <th className="py-1 px-3 bg-gray-200 text-gray-600 uppercase text-[10px] text-center">
                            Move
                        </th>
                        <th className="py-1 px-3 bg-gray-200 text-gray-600 uppercase text-[10px] text-center">
                            Clients
                        </th>
                        {advisorBreakdowns.map(ub => (
                            <React.Fragment key={ub.userId}>
                                <th className="py-1 px-3 bg-gray-200 text-gray-600 uppercase text-[10px] text-center border-l border-gray-300">
                                    Reach
                                </th>
                                <th className="py-1 px-3 bg-gray-200 text-gray-600 uppercase text-[10px] text-center">
                                    Move
                                </th>
                                <th className="py-1 px-3 bg-gray-200 text-gray-600 uppercase text-[10px] text-center">
                                    Clients
                                </th>
                            </React.Fragment>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {teamStages.map((stage, i) => (
                        <tr
                            key={stage.stageId}
                            className={`border-b border-gray-200 hover:bg-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                            <td className="py-2.5 px-3">
                                <div className="text-sm font-medium text-zinc-950">
                                    {stage.stageName}
                                </div>
                                {stage.tags.length > 0 ? (
                                    <div className="flex gap-1 mt-1">
                                        {stage.tags.map(tag => {
                                            const style = TAG_STYLES[tag];
                                            return style ? (
                                                <span
                                                    key={tag}
                                                    className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${style.bg} ${style.text}`}>
                                                    {style.label}
                                                </span>
                                            ) : null;
                                        })}
                                    </div>
                                ) : null}
                            </td>
                            <td className="py-2.5 px-3 text-sm text-center tabular-nums text-zinc-700 border-l border-gray-200">
                                {stage.reachRate}%
                            </td>
                            <td className="py-2.5 px-3 text-sm text-center tabular-nums text-zinc-700">
                                {stage.moveRate}%
                            </td>
                            <td className="py-2.5 px-3 text-sm text-center tabular-nums text-zinc-700">
                                {stage.clientCount}
                            </td>
                            {advisorBreakdowns.map(ub => {
                                const userStage = userStageMaps
                                    .get(ub.userId)
                                    ?.get(stage.stageId);
                                const hasClients = ub.totalClients > 0;
                                return (
                                    <React.Fragment key={ub.userId}>
                                        <td className="py-2.5 px-3 text-sm text-center tabular-nums text-zinc-950 border-l border-gray-200">
                                            {hasClients ? (
                                                <>
                                                    {userStage?.reachRate ?? 0}%
                                                    <VarianceBadge
                                                        advisorRate={
                                                            userStage?.reachRate ?? 0
                                                        }
                                                        teamRate={stage.reachRate}
                                                    />
                                                </>
                                            ) : (
                                                <span className="text-gray-400">
                                                    {"\u2014"}
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-2.5 px-3 text-sm text-center tabular-nums text-zinc-950">
                                            {hasClients ? (
                                                <>
                                                    {userStage?.moveRate ?? 0}%
                                                    <VarianceBadge
                                                        advisorRate={
                                                            userStage?.moveRate ?? 0
                                                        }
                                                        teamRate={stage.moveRate}
                                                    />
                                                </>
                                            ) : (
                                                <span className="text-gray-400">
                                                    {"\u2014"}
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-2.5 px-3 text-sm text-center tabular-nums text-zinc-950">
                                            {userStage?.clientCount ?? 0}
                                        </td>
                                    </React.Fragment>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const InsightStrip: React.FC<{ insights: string[] }> = ({ insights }) => {
    return (
        <div className="rounded-lg px-4 py-3" style={{ backgroundColor: "#1A1A18" }}>
            <h3 className="text-xs font-medium text-gray-400 mb-2">Insights</h3>
            <ul className="space-y-1">
                {insights.map((insight, i) => (
                    <li key={i} className="text-sm text-gray-200">
                        {insight}
                    </li>
                ))}
            </ul>
        </div>
    );
};
